import React from "react";

/**
 * ToggleSwitchMC component renders a customizable toggle switch with optional label.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isOn - Indicates whether the switch is on or off.
 * @param {function} props.handleToggle - Function to handle the toggle action.
 * @param {string} [props.size="small"] - Size of the switch, can be "small", "medium", or "large".
 * @param {string} [props.label] - Optional label text to display next to the switch.
 * @param {string} [props.labelClassName=""] - Additional class names for the label.
 * @param {boolean} [props.disabled=false] - Whether the toggle is disabled.
 *
 * @returns {JSX.Element} The rendered ToggleSwitchMC component.
 */
const ToggleSwitchMC = ({
  isOn,
  handleToggle,
  size = "small",
  label,
  labelClassName = "",
  disabled = false
}) => {
  // Size configuration using your specifications
  const sizeConfig = {
    small: {
      container: "tw-w-[21px] tw-h-[14px]", // 21px x 14px
      thumb: "tw-w-[6px] tw-h-[6px]", // 6px diameter
      translate: isOn ? "tw-translate-x-[12px]" : "tw-translate-x-[3px]"
    },
    medium: {
      container: "tw-w-[31.5px] tw-h-[21px]", // 1.5× small
      thumb: "tw-w-[9px] tw-h-[9px]", // 1.5× small thumb
      translate: isOn ? "tw-translate-x-[18px]" : "tw-translate-x-[4.5px]"
    },
    large: {
      container: "tw-w-[42px] tw-h-[28px]", // 2× small
      thumb: "tw-w-[12px] tw-h-[12px]", // 2× small thumb
      translate: isOn ? "tw-translate-x-[24px]" : "tw-translate-x-[6px]"
    }
  };

  const { container, thumb, translate } = sizeConfig[size];

  return (
    <div className="tw-flex tw-items-center">
      {/* Switch container */}
      <div
        className={`tw-relative tw-inline-flex tw-items-center ${container} tw-rounded-full tw-transition-colors tw-duration-300 ${
          disabled ? "tw-cursor-not-allowed tw-opacity-50" : "tw-cursor-pointer"
        } ${
          isOn ? "tw-bg-green-400" : "tw-bg-gray-300"
        }`}
        onClick={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          handleToggle(!isOn);
        }}
        role="switch"
        aria-checked={isOn}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {/* Sliding thumb */}
        <div
          className={`${thumb} ${translate} tw-bg-white tw-rounded-full tw-shadow-sm tw-transform tw-transition-transform tw-duration-300`}
        />
      </div>

      {/* Optional label */}
      {label && (
        <span className={`tw-ml-2 tw-text-sm ${labelClassName}`}>
          {label}
        </span>
      )}
    </div>
  );
};

export default ToggleSwitchMC;
